import PhoneCallIcon from "@redotech/redo-icons/phone-call.svg";
import PhoneXIcon from "@redotech/redo-icons/phone-x.svg";
import PhoneIcon from "@redotech/redo-icons/phone.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { UserCallAvailability } from "@redotech/redo-model/support/voice/voice-types";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import * as phoneWidgetCss from "@redotech/redo-web/voice/phone-widget.module.css";
import { TimeElapsed } from "@redotech/redo-web/voice/shared/active-call-elapsed-time";
import { PhoneWidgetStatus } from "@redotech/redo-web/voice/shared/phone-status";
import { memo, useContext } from "react";
import { VoiceContextV2 } from "./voice-context-v2";

/**
 * V2 collapsed pill. Mirrors `redo-web/voice/collapsed-phone-widget.tsx`
 * but reads from `VoiceContextV2` so accept/reject buttons appear on
 * incoming calls and the hangup button appears on active calls while
 * the widget is minimized.
 */
export const CollapsedPhoneWidgetV2 = memo(function CollapsedPhoneWidgetV2({
  onClose,
  onExpand,
}: {
  onClose(): void;
  onExpand(): void;
}) {
  return (
    <Flex align="center" justify="space-between" px="xl" py="sm">
      <Flex align="center" overflow="hidden">
        <CollapsedContent />
      </Flex>
      <CollapsedControls onClose={onClose} onExpand={onExpand} />
    </Flex>
  );
});

const CollapsedContent = memo(function CollapsedContent() {
  const ctx = useContext(VoiceContextV2);
  if (ctx == null) {
    throw new Error("VoiceContextV2 missing");
  }

  if (ctx.incoming) {
    const callerLabel = ctx.incoming.fromNumber ?? "Unknown caller";
    return (
      <>
        <Flex>
          <RedoButton
            hierarchy="primary"
            IconLeading={PhoneCallIcon}
            onClick={ctx.acceptIncoming}
            theme="success"
          />
          <RedoButton
            hierarchy="primary"
            IconLeading={PhoneXIcon}
            onClick={ctx.rejectIncoming}
            theme="destructive"
          />
        </Flex>
        <Flex dir="column" overflow="hidden">
          <Text fontSize="xxs" fontWeight="regular" textColor="primary">
            Incoming call
          </Text>
          <Text
            fontSize="xxs"
            fontWeight="regular"
            overflow="hidden"
            textColor="secondary"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {callerLabel}
          </Text>
        </Flex>
      </>
    );
  }

  if (ctx.activeCall) {
    const call = ctx.activeCall;
    const displayName = call.customer?.name ?? call.remoteNumber;
    const showTimer = call.bridged && call.startedAt != null;
    return (
      <>
        <Flex>
          <RedoButton
            hierarchy="primary"
            IconLeading={PhoneXIcon}
            onClick={ctx.hangup}
            theme="destructive"
          />
        </Flex>
        <Flex align="center" overflow="hidden">
          {showTimer && call.startedAt && (
            <TimeElapsed startTime={call.startedAt} />
          )}
          <Text
            fontSize="xxs"
            overflow="hidden"
            textColor="secondary"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {showTimer ? ` - ${displayName}` : displayName}
          </Text>
        </Flex>
      </>
    );
  }

  // Idle — match v1's icon + status pill.
  const v1Status =
    ctx.connectionStatus === "ready"
      ? UserCallAvailability.AVAILABLE
      : UserCallAvailability.UNAVAILABLE;
  return (
    <>
      <Flex align="center" className={phoneWidgetCss.phoneIcon}>
        <PhoneIcon />
      </Flex>
      <Flex align="center">
        <PhoneWidgetStatus status={v1Status} />
      </Flex>
    </>
  );
});

const CollapsedControls = memo(function CollapsedControls({
  onExpand,
  onClose,
}: {
  onExpand: () => void;
  onClose: () => void;
}) {
  return (
    <Flex gap="none">
      <RedoButton IconLeading={PlusIcon} onClick={onExpand} size="xs" />
      <RedoButton IconLeading={XIcon} onClick={onClose} size="xs" />
    </Flex>
  );
});
